import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Trip.module.css';
import AdBanner from '../../components/Banner/AdBanner'; // Import AdBanner
import tripImg from '../../assets/pic/pic1.png'; // Placeholder trip image
import useDataStore from '../../stores/useDataStore'; // To fetch chat rooms
import { getChatRooms } from '../../apis/chatApi'; // API call
import { getMyPlans } from '../../apis/aiApi'; // AI Plans API
import TripDetailModal from './TripDetailModal';

const Trip = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { chatRooms, setChatRooms, tripPlansByRoom } = useDataStore();
    const [activeIndex, setActiveIndex] = useState(0);
    const [detail, setDetail] = useState(null);
    const focusRoomKey = location?.state?.roomId ?? location?.state?.roomKey ?? null;
    const focusRef = useRef({ key: null, applied: false });
    const [apiPlansByRoom, setApiPlansByRoom] = useState({}); // API에서 가져온 계획들

    // Fetch chat rooms
    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const data = await getChatRooms();
                if (Array.isArray(data)) {
                    setChatRooms(data);
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchRooms();
    }, [setChatRooms]);

    // Fetch plans from API for each room (모든 멤버의 계획 조회)
    useEffect(() => {
        const fetchPlansForRooms = async () => {
            if (!chatRooms || chatRooms.length === 0) return;

            const plansByRoom = {};
            for (const room of chatRooms) {
                const roomId = room.roomId ?? room.id ?? room.room_id;
                if (!roomId) continue;

                try {
                    const plans = await getMyPlans(roomId);
                    if (Array.isArray(plans) && plans.length > 0) {
                        // 가장 최근 계획 사용
                        const latestPlan = plans[0];
                        const planData = latestPlan?.plan ?? latestPlan;
                        if (planData?.schedule) {
                            plansByRoom[String(roomId)] = {
                                ...planData,
                                planId: latestPlan?.id,
                                userId: latestPlan?.userId,
                            };
                        }
                    }
                } catch (e) {
                    console.warn(`Failed to fetch plans for room ${roomId}:`, e);
                }
            }
            setApiPlansByRoom(plansByRoom);
        };
        fetchPlansForRooms();
    }, [chatRooms]);

    // 로컬 스토어와 API 데이터 병합 (로컬 우선, API 데이터로 보완)
    const mergedPlansByRoom = {
        ...apiPlansByRoom,      // API에서 가져온 다른 멤버 포함 계획
        ...tripPlansByRoom,     // 로컬 데이터가 있으면 덮어씀
    };

    // Handle empty state vs trip state
    const baseRooms = chatRooms || [];
    const planFallbackRooms = Object.entries(mergedPlansByRoom || {}).reduce((acc, [roomId, plan]) => {
        const exists = baseRooms.some((room) => String(room.roomId ?? room.id ?? room.room_id) === roomId);
        if (exists) return acc;
        acc.push({
            roomId,
            name: plan?.title || '저장된 여행',
            startDate: plan?.schedule?.length ? `${plan.schedule.length}일 일정` : '저장된 일정',
            _fromPlanOnly: true,
        });
        return acc;
    }, []);
    const rooms = [...baseRooms, ...planFallbackRooms];

    useEffect(() => {
        const key = focusRoomKey === undefined || focusRoomKey === null ? null : String(focusRoomKey);
        if (!key) {
            focusRef.current = { key: null, applied: false };
            return;
        }
        if (focusRef.current.key !== key) {
            focusRef.current = { key, applied: false };
        }
    }, [focusRoomKey]);

    useEffect(() => {
        const key = focusRoomKey === undefined || focusRoomKey === null ? null : String(focusRoomKey);
        if (!key || focusRef.current.applied) return;

        const targetIndex = rooms.findIndex((room) => {
            const roomId = room?.roomId ?? room?.room_id ?? room?.id;
            if (roomId === undefined || roomId === null) return false;
            return String(roomId) === key;
        });

        if (targetIndex === -1) return;
        if (targetIndex !== activeIndex) {
            setActiveIndex(targetIndex);
        }
        focusRef.current.applied = true;
    }, [focusRoomKey, rooms, activeIndex]);

    useEffect(() => {
        if (rooms.length === 0 && activeIndex !== 0) {
            setActiveIndex(0);
        } else if (rooms.length > 0 && activeIndex >= rooms.length) {
            setActiveIndex(rooms.length - 1);
        }
    }, [rooms.length, activeIndex]);

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext();
        } else if (isRightSwipe) {
            handlePrev();
        }
    };

    const handleNext = () => {
        setActiveIndex((prev) => (prev < rooms.length - 1 ? prev + 1 : 0));
    };

    const handlePrev = () => {
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : rooms.length - 1));
    };

    // For click on card to bring to front?
    const handleCardClick = (index) => {
        if (index !== activeIndex) {
            setActiveIndex(index);
        }
    };

    // To enter chat room
    const enterRoom = (e, roomId) => {
        e.stopPropagation(); // prevent card click
        navigate('/chatroom', { state: { roomId } });
    };

    const handleViewAll = (e, room, savedPlan, roomLabel) => {
        e.stopPropagation();
        if (!savedPlan) {
            alert('저장된 여행 루트가 없습니다. 채팅방에서 루트를 설정해주세요.');
            return;
        }
        setDetail({
            plan: savedPlan,
            roomName: roomLabel,
        });
    };

    const closeDetail = () => setDetail(null);

    return (
        <div className={styles.container}>
            {/* Banner Section */}
            <div style={{ width: '100%', maxWidth: '350px' }}>
                <AdBanner />
            </div>

            <div style={{ height: '20px' }}></div>

            {rooms.length === 0 ? (
                /* Empty State */
                <div className={styles.emptyCard}>
                    <div className={styles.emptyText}>
                        참여중인 여행톡이 없어요<br />
                        새로운 여행톡을 만들고<br />
                        ai 여행 일정추천을 받아보세요!
                    </div>
                    <button type="button" className={styles.createBtn} onClick={() => navigate('/chatlist')}>
                        새로운 여행계획 시작하기
                    </button>
                </div>
            ) : (
                /* Active Trip Stack */
                <>
                    <div
                        className={styles.cardStackContainer}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        {rooms.map((room, index) => {

                            // We need to handle the case where we cycle through. 
                            // Simple approach: Render ALL rooms but apply styles based on distance.
                            // Better approach for stack: Only render active + next few.
                            // But React needs stable keys.

                            // Let's act like a deck: 
                            // Active is top. 
                            // +1 is behind.
                            // +2 is further behind.
                            // How to handle "previous"? Usually stack removes top to reveal next.

                            // Modification: Let's simply assign a "visual index" relative to active.
                            let visualIndex = index - activeIndex;
                            if (visualIndex < 0) visualIndex += rooms.length; // cycle

                            // We only show top 3 cards properly?
                            // User request: "Rotate back parts to overlap like Figma".

                            let dataIndex = 'hidden';
                            if (visualIndex === 0) dataIndex = 0;
                            else if (visualIndex === 1) dataIndex = 1;
                            else if (visualIndex === 2) dataIndex = 2;
                            // else hidden

                            const roomId = room.roomId || room.room_id || room.id || index;
                            const planKey = roomId !== undefined && roomId !== null ? String(roomId) : null;
                            const savedPlan = planKey && mergedPlansByRoom ? mergedPlansByRoom[planKey] : null;
                            const flattenedPlaces = Array.isArray(savedPlan?.schedule)
                                ? savedPlan.schedule.flatMap((day) => Array.isArray(day?.places) ? day.places : [])
                                : [];
                            const previewPlaces = flattenedPlaces
                                .map((place) => place?.name)
                                .filter(Boolean)
                                .slice(0, 3);
                            const routePreview = previewPlaces.join(' → ');
                            const placeCount = flattenedPlaces.length;
                            const dayCount = Array.isArray(savedPlan?.schedule) ? savedPlan.schedule.length : 0;
                            const roomLabel = room.name || savedPlan?.title || '알 수 없는 여행';

                            return (
                                <div
                                    key={roomId}
                                    className={styles.stackCard}
                                    data-index={dataIndex}
                                    onClick={() => handleCardClick(index)}
                                >
                                    <div className={styles.cardHeader}>
                                        <div>
                                            {/* Room Name & Date fallback */}
                                            <h2 className={styles.tripTitle}>{roomLabel}</h2>
                                            {/* Mock Date if not present */}
                                            <div className={styles.tripDate}>
                                                {savedPlan?.title || room.startDate || '일정 미정'}
                                                {savedPlan && dayCount ? ` · ${dayCount}일차` : ''}
                                            </div>
                                        </div>
                                        <button
                                            className={styles.viewAllBtn}
                                            onClick={(e) => handleViewAll(e, room, savedPlan, roomLabel)}
                                        >
                                            여행 전체보기 →
                                        </button>
                                    </div>

                                    <div className={styles.statusText}>
                                        {savedPlan ? (
                                            <>
                                                <span className={styles.statusHighlight}>● 루트 설정됨</span><br />
                                                {routePreview || `${placeCount || 0}곳 일정이 저장되었습니다.`}
                                            </>
                                        ) : (
                                            <>
                                                <span className={styles.statusHighlight}>● 일정 생성중..</span><br />
                                                여행 구성원들과 대화를 더 나눠보세요.
                                            </>
                                        )}
                                    </div>

                                    {/* Trip Image - Use placeholder or room image if available */}
                                    <img src={tripImg} alt="Trip" className={styles.tripImage} />

                                    <button className={styles.enterBtn} onClick={(e) => enterRoom(e, roomId)}>
                                        '{roomLabel}' 여행톡방 가기
                                        <span>→</span>
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Dots / Pagination */}
                    <div className={styles.dots}>
                        {rooms.map((_, idx) => (
                            <div
                                key={idx}
                                className={`${styles.dot} ${idx === activeIndex ? styles.active : ''}`}
                                onClick={() => setActiveIndex(idx)}
                            ></div>
                        ))}
                    </div>
                </>
            )}

            {detail && (
                <TripDetailModal
                    plan={detail.plan}
                    roomName={detail.roomName}
                    onClose={closeDetail}
                />
            )}
        </div>
    );
};

export default Trip;
